import {
  SET_STEP_ONE_DATA,
  SET_STEP_TWO_DATA,
  SET_USER_ID,
  RESET_USER_CREATE_DATA,
} from './UserCreate.constant';
import { IUserCreateState, UserCreateActionsTypes } from './UserCreate.type';
import {
  saveFileToIndexedDB,
  clearAllFilesFromIndexedDB,
} from 'utils/indexedDB';

const USER_CREATE_STORAGE_KEY = 'userCreateData';

const saveToLocalStorage = async (state: IUserCreateState) => {
  try {
    const filePromises = state.stepTwo.details.map(async (detail, index) => {
      if (detail.file) {
        const fileId = `object-${index}`;
        await saveFileToIndexedDB(fileId, detail.file);
        return fileId;
      }
      return null;
    });

    const fileIds = await Promise.all(filePromises);

    const serializableState = {
      ...state,
      stepTwo: {
        ...state.stepTwo,
        details: state.stepTwo.details.map((detail, index) => ({
          ...detail,
          fileId: fileIds[index],
          file: undefined,
          zones: (detail.zones || []).map((zone) => ({
            ...zone,
            photo: undefined,
          })),
        })),
      },
    };

    localStorage.setItem(
      USER_CREATE_STORAGE_KEY,
      JSON.stringify(serializableState),
    );
  } catch (error) {
    console.warn('Failed to save user create data to localStorage:', error);
  }
};

const loadFromLocalStorage = (): Partial<IUserCreateState> | null => {
  try {
    const stored = localStorage.getItem(USER_CREATE_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        ...parsed,
        stepTwo: {
          ...parsed.stepTwo,
          details: parsed.stepTwo.details.map((detail: any) => ({
            ...detail,
            file: null,
            zones: (detail.zones || []).map((zone: any) => ({
              ...zone,
              photo: undefined,
            })),
          })),
        },
      };
    }
  } catch (error) {
    console.warn('Failed to load user create data from localStorage:', error);
  }
  return null;
};

const initialZones = [
  {
    name: '',
    plantType: '',
    plantName: '',
    soilType: '',
    sensors: [],
  },
];

const initialDetails = [
  {
    name: '',
    file: undefined,
    zones: initialZones,
    sensors: [],
  },
];

const savedData = loadFromLocalStorage();

const getEmptyState = (): IUserCreateState => ({
  stepOne: {
    fullname: '',
    email: '',
    password: '',
    phoneCode: 'ae',
    phoneNumber: '',
    whatsappNotifications: false,
  },
  stepTwo: {
    details: initialDetails,
  },
});

const initialState: IUserCreateState = savedData
  ? {
      ...savedData,
      stepOne: {
        fullname: savedData.stepOne?.fullname || '',
        email: savedData.stepOne?.email || '',
        password: savedData.stepOne?.password || '',
        phoneCode: savedData.stepOne?.phoneCode || 'ae',
        phoneNumber: savedData.stepOne?.phoneNumber || '',
        whatsappNotifications: savedData.stepOne?.whatsappNotifications ?? true,
      },
      stepTwo: {
        details: savedData.stepTwo?.details || initialDetails,
      },
    }
  : getEmptyState();

export default function userCreateReducer(
  state = initialState,
  action: UserCreateActionsTypes,
): IUserCreateState {
  let newState: IUserCreateState;

  switch (action.type) {
    case SET_STEP_ONE_DATA:
      newState = {
        ...state,
        stepOne: action.payload,
      };
      break;

    case SET_STEP_TWO_DATA:
      newState = {
        ...state,
        stepTwo: action.payload,
      };
      break;

    case SET_USER_ID:
      newState = {
        ...state,
        userId: action.payload,
      };
      break;

    case RESET_USER_CREATE_DATA:
      localStorage.removeItem(USER_CREATE_STORAGE_KEY);
      clearAllFilesFromIndexedDB().catch((error) =>
        console.warn('Failed to clear files from IndexedDB:', error),
      );
      return getEmptyState();

    default:
      return state;
  }

  saveToLocalStorage(newState);
  return newState;
}
